"use client";

import type { ILink } from "@ui/components/core/Link";
import type { Media, OmniboxDeveloperBlock } from "@mw/shared/payload-types";
import type { FC } from "react";
import type { DemoConfigDeveloper } from "@/app/_blocks/BlockOmniboxDeveloper";
import type { Key } from "react-aria-components";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { ArrowUpRightIcon } from "lucide-react";
import { Tabs, TabPanel } from "react-aria-components";

import DemoCardDeveloper from "./DemoCardDeveloper";
import { BaseLink } from "./Link";
import { TabList } from "./TabList";
import { OmniboxLayoutWrapper } from "@ui/components/demos/OmniboxLayoutWrapper";
import type { Speech } from "@/app/_blocks/BlockInteractCreateOmnibox/demos/Speech/Speech";
import type { CodeToken } from "@ui/lib/syntaxHighlighting";

// Derive types from Payload-generated types
type PayloadItem = NonNullable<OmniboxDeveloperBlock["items"]>[number];
type PayloadCodeSnippet = NonNullable<NonNullable<PayloadItem["demoConfig"]["codeSnippets"]>>[number];

export type CodeLanguage = PayloadCodeSnippet["language"];
export type CodeSnippet = PayloadCodeSnippet & { tokens: CodeToken[][] };

export interface OmniboxDeveloperItem {
  id: string;
  codeSnippets?: CodeSnippet[] | null;
  demo: DemoConfigDeveloper;
  demoDescription?: string | null;
  icon?: Media;
  link?: ILink | null;
  title: string;
}

export interface OmniboxDeveloperProps {
  className?: string;
  items: OmniboxDeveloperItem[];
  subtitle?: null | string;
  title?: string | null;
  speechProps: React.ComponentPropsWithoutRef<typeof Speech>;
}

type Mode = "demo" | "code";

export const OmniboxDeveloper: FC<OmniboxDeveloperProps> = ({
  title: _title,
  items,
  className,
  subtitle: _subtitle,
  speechProps,
}) => {
  const t = useTranslations("ClientKeys.DemoCardDeveloper");
  const [selectedKey, setSelectedKey] = useState<Key>(items[0]?.id);
  const [mode, setMode] = useState<Mode>("demo");

  const selectedItem = items.find(item => item.id === selectedKey) ?? items[0];
  const hasCodeSnippets = selectedItem?.codeSnippets && selectedItem.codeSnippets.length > 0;

  const modeItems = [
    { id: "demo" as const, title: t("demo") },
    { id: "code" as const, title: t("code") },
  ];

  const headerContent = (
    <div>
      <p className='tw-type-base tw-text-black tw-text-pretty tw-flex tw-items-center'>
        {selectedItem?.title}
        {selectedItem?.link && (
          <ArrowUpRightIcon
            className='tw-ml-2 tw-w-5 tw-h-5 tw-text-gray-400 tw-opacity-0 group-hover/header:tw-opacity-100 tw-transition-opacity tw-duration-200'
            aria-hidden='true'
          />
        )}
      </p>
      {selectedItem?.demoDescription && (
        <p className='tw-type-xs tw-text-gray-500 tw-mt-2'>{selectedItem.demoDescription}</p>
      )}
    </div>
  );

  // Transform items to TabList format
  const tabItems = items.map(item => ({ id: item.id, title: item.title }));

  return (
    <OmniboxLayoutWrapper
      height='tw-h-[36rem]'
      padding='tw-p-6'
      className={`tw-flex tw-flex-col [--full-bleed:theme(spacing.6)] ${className ?? ""}`}
    >
      {/* Header row */}
      <div className='tw-flex tw-justify-between tw-items-start'>
        {selectedItem?.link ? (
          <BaseLink href={selectedItem.link.href} target={selectedItem.link.target} className='tw-group/header'>
            {headerContent}
          </BaseLink>
        ) : (
          headerContent
        )}
        {/* Mode switcher - separate Tabs to avoid nesting */}
        {hasCodeSnippets && (
          <Tabs selectedKey={mode} onSelectionChange={key => setMode(key as Mode)}>
            <TabList items={modeItems} size='sm' surface='cream' />
          </Tabs>
        )}
      </div>

      {/* Main content area with item selection Tabs */}
      <Tabs selectedKey={selectedKey} onSelectionChange={setSelectedKey} className='tw-flex-1 tw-flex tw-flex-col'>
        <div className='tw-flex-1 tw-flex tw-items-center tw-justify-center'>
          <div className='tw-w-full tw-max-w-xl'>
            {items.map(item => (
              <TabPanel key={item.id} id={item.id} className='tw-outline-none'>
                <DemoCardDeveloper
                  animationKey={item.id}
                  codeSnippets={item.codeSnippets}
                  demo={item.demo}
                  mode={mode}
                  speechProps={speechProps}
                />
              </TabPanel>
            ))}
          </div>
        </div>

        {/* Category tabs at bottom - only show if more than one item */}
        {items.length > 1 && (
          <TabList items={tabItems} surface='cream' size='md' centered showRing={false} className='tw-order-last' />
        )}
      </Tabs>
    </OmniboxLayoutWrapper>
  );
};

export default OmniboxDeveloper;
